import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from '@api/axios'

/**
 * Hook for managing notifications
 */
export const useNotifications = (options = {}) => {
    const queryClient = useQueryClient()
    const { enabled = true, refetchInterval = 30000 } = options

    // Fetch notifications
    const {
        data: notificationsData,
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ['notifications'],
        queryFn: async () => {
            const response = await axios.get('/notifications')
            console.log('Notifications API response:', response.data)
            const data = response.data?.data || response.data
            console.log('Parsed notifications data:', data)
            return data
        },
        enabled,
        refetchInterval, // Poll every 30 seconds by default
        staleTime: 10000
    })

    // Fetch unread count
    const { data: countData } = useQuery({
        queryKey: ['notifications', 'count'],
        queryFn: async () => {
            const response = await axios.get('/notifications/count')
            console.log('Notifications count API response:', response.data)
            // Handle different response formats
            const count = response.data?.data?.count ?? response.data?.count ?? response.data?.unread_count ?? 0
            console.log('Parsed unread count:', count)
            return count
        },
        enabled,
        refetchInterval,
        staleTime: 10000
    })

    // Mark notification as read
    const markAsReadMutation = useMutation({
        mutationFn: async (id) => {
            await axios.put(`/notifications/${id}/read`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
        }
    })

    // Mark all as read
    const markAllAsReadMutation = useMutation({
        mutationFn: async () => {
            await axios.put('/notifications/read-all')
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
        }
    })

    return {
        notifications: notificationsData?.notifications || [],
        unreadCount: countData || 0,
        isLoading,
        error,
        refetch,
        markAsRead: markAsReadMutation.mutate,
        markAllAsRead: markAllAsReadMutation.mutate,
        isMarkingRead: markAsReadMutation.isPending,
        isMarkingAllRead: markAllAsReadMutation.isPending
    }
}

export default useNotifications
